import Cookies from 'js-cookie';
import FetchData from '../Components/ServerCall/FetchData';
import { IuserList, IUserData, IUserDetails } from './types';

/**
 * @returns an object with isError, message and data keys according to response status.
 * for success status, data is list of all users.
 */
export async function getUsersList() {
  const response = await FetchData({
    requestType: 'noData',
    method: 'GET',
    url: 'users/list',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${Cookies.get('authToken')}`,
      // "X-TENANT-ID": "silzila",
    },
  });
  if (!response.status) {
    return {
      isError: true,
      message: (response.error ?? 'Error while fetching users list') as string,
      data: null,
    };
  }
  return {
    isError: false,
    message: 'Users list retrieved successfully',
    data: response.data as IuserList[],
  };
}

/**
 *
 * @param isUpdating     boolean value which tells if api url is for updating or for creating. isUpdating is true for update user and false for create user.
 * @param formData       user data
 * @returns an object with isError, message and data keys according to response status.
 */
export async function createOrUpdateUser(isUpdating: boolean, formData: FormData) {
  const apiUrl = isUpdating ? 'user/update' : 'user/add';
  const method = isUpdating ? 'PUT' : 'POST';

  const response = await FetchData({
    requestType: 'withData',
    method,
    url: apiUrl,
    headers: {
      Authorization: `Bearer ${Cookies.get('authToken')}`,
      'Content-Type': 'multipart/form-data',
    },
    data: formData,
  });

  if (!response.status) {
    return {
      isError: true,
      message: (response.error ||
        response.data ||
        `Some error has occoured while ${isUpdating ? 'updating' : 'creating'} user. Please try again later`) as string,
      data: null,
    };
  }
  return {
    isError: false,
    message: `User ${isUpdating ? 'details updated' : 'added'} successfully`,
    data: response.data as IUserData,
  };
}

/**
 *
 * @param id            unique id of user
 * @returns an object with isError, message and data keys according to response status.
 */
export async function deleteUser(id: string) {
  const response = await FetchData({
    requestType: 'noData',
    method: 'DELETE',
    url: `user/delete/${id}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${Cookies.get('authToken')}`,
      // 'X-TENANT-ID': 'silzila',
    },
  });
  if (!response.status) {
    return {
      isError: true,
      message: (response.error ?? 'Failed to delete user') as string,
      data: null,
    };
  }
  return {
    isError: false,
    message: 'User deleted successfully',
    data: response.data as string,
  };
}

/**
 *
 * @param id            unique id of a user
 * @param isActive      boolean value which tells a user is active or not
 * isActive is true if user is active else false.
 * @returns an object with isError, message and data keys according to response status.
 */
export async function deactivateUser(id: string, isActive: boolean) {
  const response = await FetchData({
    requestType: 'noData',
    method: 'PUT',
    url: `user/deactivate?userId=${id}&deactivate=${isActive}`,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${Cookies.get('authToken')}`,
      // 'X-TENANT-ID': 'silzila',
    },
  });
  if (!response.status) {
    return {
      isError: true,
      message: (response.error ??
        `Failed to ${isActive ? 'deactivate' : 'activate'} user`) as string,
      data: null,
    };
  }
  return {
    isError: false,
    message: `User ${isActive ? 'deactivated' : 'activated'} successfully`,
    data: response.data as string,
  };
}

/**
 *
 * @param userIds    unique id of user
 * @param groupName  name of the new group
 * @returns an object with isError, message and data keys according to response status.
 */
export async function createNewGroupForMultipleUsers(userIds: any, groupName: string) {
  const requestBody = {
    userIds: userIds,
    groupName: groupName,
  };
  const response = await FetchData({
    requestType: 'withData',
    method: 'POST',
    url: 'group-users/create',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${Cookies.get('authToken')}`,
      // "X-TENANT-ID": "silzila",
    },
    data: requestBody,
  });

  if (!response.status) {
    return {
      isError: true,
      message: (response.error ?? `Failed to create group for users`) as string,
      data: null,
    };
  }
  return {
    isError: false,
    message: `Group ${groupName} created successfully with users: ${userIds}`,
    data: response.data as string,
  };
}

/**
 *
 * @returns an object with isError, message and data keys according to response status.
 * for success status, data is personal data of a user.
 */
export async function getUserDetails() {
  const response = await FetchData({
    requestType: 'noData',
    method: 'GET',
    url: 'user-details',
    headers: {
      Authorization: `Bearer ${Cookies.get('authToken')}`,
    },
  });

  if (!response.status) {
    return {
      isError: true,
      message: (response.error ?? 'Error while fetching User Details') as string,
      data: null,
    };
  }
  return {
    isError: false,
    message: 'User data fetched successfully',
    data: response.data as IUserDetails,
  };
}

/**
 *
 * @param id    unique id of user
 * @returns an object with isError, message and data keys according to response status.
 */
export async function fetchUserProfilePicture(id: string) {
  const response = await FetchData({
    requestType: 'withData',
    method: 'GET',
    url: 'user/profile-image',
    data: { userId: id },
    headers: {
      Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.status) {
    return {
      isError: true,
      message: (response.error ?? 'Error while fetching Profile Image') as string,
      data: null,
    };
  }
  return {
    isError: false,
    message: 'profile fetched successfully',
    data: response.data as string,
  };
}
